import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { CalendarIcon, SearchIcon } from 'src/v3/core/icons'
import { FilterList as FilterIcon } from '@mui/icons-material'
import {
  // Actions
  setSelectedLinkedServiceID,
  setViewMode,
  setTimeRange,
  setCustomStartDate,
  setCustomEndDate,
  setSelectedUsers,
  setSelectedCategories,
  setSelectedLicenseTypes,
  setSelectedGroups,
  setAlertMessage,
  resetAllFilters,
  // Selectors
  selectSelectedLinkedServiceID,
  selectViewMode,
  selectTimeRange,
  selectCustomDateRange,
  selectSelectedUsers,
  selectSelectedCategories,
  selectSelectedLicenseTypes,
  selectSelectedGroups,
  selectCategories,
  selectLicenseTypes,
  // Types
  TimeRange,
} from '../../index'
import {
  selectLinkedServices,
  selectUsersByLinkedService,
  selectGroupsByLinkedService,
} from '../../../../manage.slice'

export default function FilterPanel() {
  let dispatch = useAppDispatch()

  // Redux state selectors
  let linkedServices = useAppSelector(selectLinkedServices)
  let selectedLinkedServiceID = useAppSelector(selectSelectedLinkedServiceID)
  let users = useAppSelector(
    selectUsersByLinkedService(selectedLinkedServiceID),
  )
  let groups = useAppSelector(
    selectGroupsByLinkedService(selectedLinkedServiceID),
  )

  let viewMode = useAppSelector(selectViewMode)
  let timeRange = useAppSelector(selectTimeRange)
  let customDateRange = useAppSelector(selectCustomDateRange)
  let customStartDate = customDateRange.startDate
    ? dayjs(customDateRange.startDate)
    : null
  let customEndDate = customDateRange.endDate
    ? dayjs(customDateRange.endDate)
    : null

  let selectedUsers = useAppSelector(selectSelectedUsers)
  let selectedCategories = useAppSelector(selectSelectedCategories)
  let selectedLicenseTypes = useAppSelector(selectSelectedLicenseTypes)
  let selectedGroups = useAppSelector(selectSelectedGroups)

  let categories = useAppSelector(selectCategories)
  let licenseTypes = useAppSelector(selectLicenseTypes)

  // Reset all filters function
  let handleResetAllFilters = () => {
    dispatch(resetAllFilters())
  }

  return (
    <Card sx={{ mb: 4, p: 3 }}>
      <Box className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* Linked Service Selection */}
        <Box>
          <FormControl fullWidth>
            <InputLabel>Select Linked Service</InputLabel>
            <Select
              value={selectedLinkedServiceID}
              onChange={(e) =>
                dispatch(setSelectedLinkedServiceID(e.target.value))
              }
              label="Select Linked Service"
              startAdornment={<SearchIcon sx={{ mr: 1 }} />}
            >
              {(linkedServices as any[]).map((service: any) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Time Range */}
        <Box>
          <FormControl fullWidth>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) =>
                dispatch(setTimeRange(e.target.value as TimeRange))
              }
              startAdornment={<CalendarIcon sx={{ mr: 1 }} />}
            >
              <MenuItem value="week">Last Week</MenuItem>
              <MenuItem value="month">Last Month</MenuItem>
              <MenuItem value="quarter">Last Quarter</MenuItem>
              <MenuItem value="year">Last Year</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* View Mode */}
        <Box>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && dispatch(setViewMode(newMode))}
            fullWidth
          >
            <ToggleButton value="timeline">Timeline</ToggleButton>
            <ToggleButton value="analytics">Analytics</ToggleButton>
            <ToggleButton value="summary">Summary</ToggleButton>
            <ToggleButton value="export">Export</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      {/* Custom Date Range */}
      {timeRange === 'custom' && (
        <Box className="grid grid-cols-1 gap-6 mt-6 md:grid-cols-2">
          <DatePicker
            label="Start Date"
            value={customStartDate}
            onChange={(newValue) =>
              dispatch(setCustomStartDate(newValue?.toISOString() || null))
            }
            slotProps={{ textField: { fullWidth: true } }}
          />
          <DatePicker
            label="End Date"
            value={customEndDate}
            onChange={(newValue) =>
              dispatch(setCustomEndDate(newValue?.toISOString() || null))
            }
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Box>
      )}
      {/* Advanced Filters */}
      <Card variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <FilterIcon />
            Advanced Filters
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={handleResetAllFilters}
            sx={{ minWidth: 'auto' }}
          >
            Reset Filters
          </Button>
        </Box>
        <Box className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {/* User Filter */}
          <Box>
            <Autocomplete
              multiple
              options={users}
              getOptionLabel={(option) =>
                `${option.name} (${option.primaryEmail})`
              }
              value={(users as any[]).filter((user) =>
                selectedUsers.includes(user.id),
              )}
              onChange={(_, value) =>
                dispatch(setSelectedUsers(value.map((user) => user.id)))
              }
              disableCloseOnSelect
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options.slice(0, 100) // Limit initial display to 100 users
                return options
                  .filter(
                    (option) =>
                      option.name
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()) ||
                      option.primaryEmail
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()),
                  )
                  .slice(0, 100) // Limit search results to 100 users
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Users"
                  placeholder="Search users..."
                  helperText={`${(users as any[]).length} users available`}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.primaryEmail}
                    </Typography>
                  </Box>
                </li>
              )}
              renderValue={(value, getItemProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    size="small"
                    {...getItemProps({ index })}
                  />
                ))
              }
              slotProps={{
                listbox: {
                  style: { maxHeight: 300 },
                },
              }}
            />
          </Box>

          {/* Category Filter */}
          <Box>
            <Autocomplete
              multiple
              options={categories}
              value={selectedCategories}
              onChange={(_, value) => dispatch(setSelectedCategories(value))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Categories"
                  placeholder="Select categories..."
                />
              )}
              renderValue={(value, getItemProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    color="primary"
                    {...getItemProps({ index })}
                  />
                ))
              }
            />
          </Box>

          {/* License Type Filter */}
          <Box>
            <Autocomplete
              multiple
              options={licenseTypes}
              value={selectedLicenseTypes}
              onChange={(_, value) => {
                if (value.length > 0 && selectedGroups.length > 0) {
                  dispatch(setSelectedGroups([]))
                  dispatch(
                    setAlertMessage(
                      'Group filter was cleared because license types were selected',
                    ),
                  )
                }
                dispatch(setSelectedLicenseTypes(value))
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by License Types"
                  placeholder="Select license types..."
                />
              )}
              renderValue={(value, getItemProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    color="secondary"
                    {...getItemProps({ index })}
                  />
                ))
              }
            />
          </Box>

          {/* Group Filter */}
          <Box>
            <Autocomplete
              multiple
              options={groups}
              getOptionLabel={(option) => option.name}
              value={(groups as any[]).filter((group) =>
                selectedGroups.includes(group.id || ''),
              )}
              onChange={(_, value) => {
                if (value.length > 0 && selectedLicenseTypes.length > 0) {
                  dispatch(setSelectedLicenseTypes([]))
                  dispatch(
                    setAlertMessage(
                      'License filter was cleared because a group was selected',
                    ),
                  )
                }
                dispatch(
                  setSelectedGroups(value.map((group) => group.id || '')),
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Groups"
                  placeholder="Select groups..."
                />
              )}
              renderValue={(value, getItemProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    size="small"
                    color="success"
                    {...getItemProps({ index })}
                  />
                ))
              }
            />
          </Box>
        </Box>
      </Card>
    </Card>
  )
}
